import { useEffect, useState } from "react";
import { USER_GROUPS } from "constants/roles";

import { useRouteMatch } from "react-router-dom";
import { RangeOfMotion } from "./containers/RangeOfMotion";
import { ScoreBlock } from "./containers/ScoreBlock";
import { ExamNotesList } from "./containers/ExamNotesList";
import { AthleteTopSection } from "./components/Athlete/AthleteTopSection";
import { ConfidenceScoreBadge } from "./components/ConfidenceScoreBadge";
import { PatientTopSection } from "./components/Patient/PatientTopSection";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { getExam } from "../../store/ExamStore/actions";
import { getMinAndMaxExamFrames } from "../../store/ExamMLResultStore/actions";
import { selectRequests } from "../../store/LoadingStore/selectors";
import { Requests } from "../../types/requests";
import { VideoComponent } from "./containers/VideoComponent";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import {
  selectExamMLResultLeftKnee,
  selectExamMLResultRightKnee,
  selectExamMLResultDoubleKnee,
  selectExamMLResultLeftKneeIds,
  selectExamMLResultLeftKneeLabels,
  selectExamMLResultLeftMaxValues,
  selectExamMLResultLeftMinValues,
  selectExamMLResultRightKneeIds,
  selectExamMLResultRightKneeLabels,
  selectExamMLResultRightMaxValues,
  selectExamMLResultRightMinValues,
} from "store/PatientStore/selectors";
import { RegionChart } from "pages/PatientDetails/components/Athlete/RegionChart";
import { RegionChartSingleKnee } from "pages/PatientDetails/components/Athlete/RegionChartSingleKnee";
import {
  initPatientDetailsPage,
  cleanUpCurrentPatientInfo,
} from "store/PatientStore/actions";
import { jointIdList } from "../../utils/formatters";
import LineRegionChart, {
  LineType,
} from "pages/PatientDetails/components/Patient/components/LineRegionChart";
import { Grid } from "@mui/material";
import ContralateralSwitch from "pages/PatientDetails/containers/components/ContralateralSwitch";

export const VideoReview = () => {
  const match = useRouteMatch<{
    patientId: string;
    jointId: string;
    id: string;
    side: string;
  }>();
  const dispatch = useAppDispatch();
  const currentClinician = useAppSelector(selectCurrentClinician);
  const examMLResultLeftKnee = useAppSelector(selectExamMLResultLeftKnee);
  const examMLResultRightKnee = useAppSelector(selectExamMLResultRightKnee);
  const examMLResultDoubleKnee = useAppSelector(selectExamMLResultDoubleKnee);
  const leftKneeIds = useAppSelector(selectExamMLResultLeftKneeIds);
  const rightKneeIds = useAppSelector(selectExamMLResultRightKneeIds);
  const leftKneeLabels = useAppSelector(selectExamMLResultLeftKneeLabels);
  const rightKneeLabels = useAppSelector(selectExamMLResultRightKneeLabels);
  const leftMaxValues = useAppSelector(selectExamMLResultLeftMaxValues);
  const rightMaxValues = useAppSelector(selectExamMLResultRightMaxValues);
  const leftMinValues = useAppSelector(selectExamMLResultLeftMinValues);
  const rightMinValues = useAppSelector(selectExamMLResultRightMinValues);
  const [currentJointId, setCurrentJointId] = useState<string>(
    match.params.jointId,
  );
  const [isContralateral, setIsContralateral] = useState(false);
  const handleContralateralToggle = (isChecked: boolean) => {
    setIsContralateral(isChecked);
  };

  const loading = useAppSelector((state) =>
    selectRequests([Requests.getExam], state),
  );

  useEffect(() => {
    dispatch(initPatientDetailsPage(match.params.patientId));
    return function cleanup() {
      setCurrentJointId("");
      dispatch(cleanUpCurrentPatientInfo());
    };
  }, [match.params.patientId]);

  useEffect(() => {
    dispatch(getMinAndMaxExamFrames(match.params?.id));
    dispatch(getExam(match.params?.id));
  }, [match.params?.id]);

  const getIds = (side: string) => {
    return side === "left" ? leftKneeIds : rightKneeIds;
  };

  const getLabels = (side: string) => {
    return side === "left" ? leftKneeLabels : rightKneeLabels;
  };

  const getData = (side: string, isMax: boolean) => {
    if (side === "left") {
      return isMax ? leftMaxValues : leftMinValues;
    } else {
      return isMax ? rightMaxValues : rightMinValues;
    }
  };

  const LineRegionChartWrapper = ({
    side,
    isContralateral,
    graphType,
  }: any) => {
    const oppositeSide = side === "left" ? "right" : "left";
    const isFlexion = LineType.Flexion === graphType;

    const ids = getIds(side);
    const labels = getLabels(side);
    const contralateralLabels = getLabels(oppositeSide);
    const data = getData(side, isFlexion);
    const contralateralData = getData(oppositeSide, isFlexion);
    const opositeChartData = getData(side, !isFlexion);

    return (
      <Grid xs={6} sx={{ height: "100%" }}>
        <LineRegionChart
          ids={ids}
          labels={labels}
          contralateralLabels={contralateralLabels}
          data={data}
          opositeChartData={opositeChartData}
          contralateralData={contralateralData}
          graphType={graphType}
          isContralateral={isContralateral}
        />
      </Grid>
    );
  };

  return (
    <div className="container max-w-[1024px] m-auto px-8 pt-[30px]">
      {(currentClinician?.role?.includes(USER_GROUPS.CLINICIAN) ||
        currentClinician?.role?.includes(USER_GROUPS.CLINIC_TEAM_MEMBER)) && (
        <PatientTopSection loading={loading} />
      )}
      {currentClinician?.role?.includes(USER_GROUPS.COACH) && (
        <AthleteTopSection
          loading={loading}
          jointId={currentJointId}
          setJointId={setCurrentJointId}
        />
      )}
      <section className="flex mt-7 space-x-8 md:flex-col md:items-center md:space-x-0">
        <div className="w-[268px] h-[580px] rounded-xl shrink-0 relative">
          <VideoComponent />
        </div>
        <div className="flex flex-col w-[70%] md:w-full md:mt-5">
          <div className="flex flex-wrap w-full justify-between sm:flex-col sm:space-y-4">
            {!window.location.search.includes("isFreeForm=true") && (
              <RangeOfMotion />
            )}
            <ConfidenceScoreBadge />
            {(currentClinician?.role?.includes(USER_GROUPS.CLINICIAN) ||
              currentClinician?.role?.includes(
                USER_GROUPS.CLINIC_TEAM_MEMBER,
              )) && <ScoreBlock loading={loading} />}
          </div>
          <>
            {!loading &&
              (!currentClinician?.role?.includes(USER_GROUPS.COACH) ? (
                <Grid
                  container
                  direction={"row"}
                  sx={{ justifyContent: "flex-end", marginY: 5 }}
                >
                  <ContralateralSwitch onToggle={handleContralateralToggle} />
                  <Grid container xs={12}>
                    <LineRegionChartWrapper
                      side={match.params.side}
                      isContralateral={isContralateral}
                      graphType={LineType.Extension}
                    />
                    <LineRegionChartWrapper
                      side={match.params.side}
                      isContralateral={isContralateral}
                      graphType={LineType.Flexion}
                    />
                  </Grid>
                </Grid>
              ) : currentJointId == "62e190ff9f293b927973f92b" ? (
                <div className="flex flex-col card w-full p-8 rounded-[20px] mt-5">
                  <RegionChart
                    examMLResultDoubleKnee={examMLResultDoubleKnee}
                    title={jointIdList[currentJointId]}
                  />
                </div>
              ) : (
                (currentJointId == "624d97375ed2b030d38e4707" ||
                  currentJointId == "624d97335ed2b030d38e4706") && (
                  <div className="flex flex-col card w-full p-8 rounded-[20px] mt-5">
                    <RegionChartSingleKnee
                      examMLResultRightKnee={examMLResultRightKnee}
                      examMLResultLeftKnee={examMLResultLeftKnee}
                      title={jointIdList[currentJointId]}
                    />
                  </div>
                )
              ))}
          </>
          <ExamNotesList />
        </div>
      </section>
    </div>
  );
};
